<template>
  <div 
    v-if="totalItems > 0" 
    class="sticky-cart-bar" 
    :class="{ 'sticky-cart-bar--visible': isVisible }"
  >
    <div class="sticky-cart-content">
      <div
        class="sticky-cart-info"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <span id="sticky-cart-count">{{ totalItems }}</span>
        <span>{{ $t('cart.items', { count: totalItems }) }}</span>
      </div>
      <div class="sticky-cart-total">
        <span id="sticky-cart-total">{{ formatCurrency(totalPrice) }}</span>
      </div>
      <button
        type="button"
        class="sticky-cart-btn"
        @click="openCart"
      >
        {{ $t('cart.view_cart') }}
      </button>
    </div>
    
    <div
      v-if="showUndo"
      id="undo-snackbar"
      class="undo-snackbar"
      :class="{ 'undo-snackbar--visible': showUndo }"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <span class="undo-snackbar__message">
        {{ $t('cart.item_removed', { item: lastRemovedItem?.name || '' }) }}
      </span>
      <button
        id="undo-btn"
        type="button"
        class="undo-btn"
        @click="undoRemove"
      >
        {{ $t('cart.undo') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useCartStore } from '../../stores/cart-store';
import type { CartItem } from '../../modules/cart-persistence';
import { formatCurrency } from '@shared/utils/currency';

const cartStore = useCartStore();

const totalItems = computed(() => cartStore.totalItems);
const totalPrice = computed(() => cartStore.totalPrice);
const isVisible = ref(false);
const showUndo = ref(false);
const lastRemovedItem = ref<CartItem | null>(null);
let undoTimeoutId: number | null = null;

// Show/hide sticky cart based on scroll and cart state
const handleScroll = () => {
  isVisible.value = window.scrollY > 100 && totalItems.value > 0;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  handleScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll);
  if (undoTimeoutId !== null) {
    window.clearTimeout(undoTimeoutId);
    undoTimeoutId = null;
  }
});

// Watch for cart changes to show undo snackbar
watch(() => cartStore.items, (newItems, oldItems) => {
  if (oldItems.length > newItems.length) {
    // An item was removed
    const removedItems = oldItems.filter(item => !newItems.some(newItem => newItem.id === item.id));
    if (removedItems.length > 0) {
      lastRemovedItem.value = removedItems[0];
      showUndo.value = true;

      if (undoTimeoutId !== null) {
        window.clearTimeout(undoTimeoutId);
      }

      // Auto-hide undo after 5 seconds.
      undoTimeoutId = window.setTimeout(() => {
        showUndo.value = false;
        undoTimeoutId = null;
      }, 5000);
    }
  }
});

const openCart = () => {
  cartStore.openCart();
};

const undoRemove = () => {
  if (lastRemovedItem.value) {
    cartStore.addItem(lastRemovedItem.value);
    showUndo.value = false;
  }
};
</script>

<style scoped>
.sticky-cart-bar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(100px);
}

.sticky-cart-bar--visible {
  opacity: 1;
  transform: translateY(0);
}

.sticky-cart-content {
  background: #3b82f6;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.sticky-cart-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sticky-cart-info span:first-child {
  background: white;
  color: #3b82f6;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: bold;
}

.sticky-cart-btn {
  background: white;
  color: #3b82f6;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
  /* #832: WCAG 2.5.5 minimum touch target */
  min-width: 44px;
  min-height: 44px;
}

.sticky-cart-btn:hover {
  background: #f0f0f0;
}

.undo-snackbar {
  position: fixed;
  bottom: 80px;
  right: 20px;
  background: #111827;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.3s ease;
}

.undo-snackbar--visible {
  opacity: 1;
  transform: translateY(0);
}

.undo-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
  /* #832: WCAG 2.5.5 minimum touch target */
  min-width: 44px;
  min-height: 44px;
}

.undo-btn:hover {
  background: #2563eb;
}
</style>
